// Chronicle event categories — muted, low-saturation premium palette.
// Colors are distinct from one another while staying calm and un-saturated.
// hex = category accent color, ink = readable text color on a light tint of that accent.
//
// SINGLE SOURCE OF TRUTH for category ids + colours. There are exactly 15 fixed,
// built-in categories: 9 PRIMARY (shown directly in the main selector) and 6
// ADDITIONAL (shown in the "Additional categories" bottom sheet). No custom /
// user-created / dynamically-managed categories exist. `group` drives which
// selector surface a category appears on; it never affects storage or the id.

export const CATEGORIES = [
  // --- 9 PRIMARY categories (unchanged ids, colours, order) ------------------
  { id: "exercise",          group: "primary", label: "Exercise",             hex: "#7FAF8A", ink: "#16291E" },
  { id: "planned_free_time", group: "primary", label: "Planned free time",    hex: "#4F7D63", ink: "#10241F" },
  { id: "own_project",       group: "primary", label: "Own project",          hex: "#D8BE72", ink: "#2B2410" },
  { id: "free_time",         group: "primary", label: "Free time / unmarked", hex: "#D8C7B2", ink: "#2A2418" },
  { id: "work",              group: "primary", label: "Work",                 hex: "#2F4266", ink: "#0F1626" },
  { id: "chores",            group: "primary", label: "Chores",               hex: "#C96E3A", ink: "#2B1409" },
  { id: "entertainment",     group: "primary", label: "Entertainment",        hex: "#8A74A8", ink: "#1E1729" },
  { id: "sleep_rest",        group: "primary", label: "Sleep / rest",         hex: "#AEB4BE", ink: "#14181F" },
  { id: "transition_break",  group: "primary", label: "Transition / break",   hex: "#B89B7A", ink: "#241C12" },
  // --- 6 ADDITIONAL categories (bottom sheet). Fixed; no management controls. -
  { id: "health_improvement", group: "additional", label: "Health improvement", hex: "#5FA6A0", ink: "#0F2422" }, // Turquoise
  { id: "social_activity",    group: "additional", label: "Social activity",    hex: "#C286A6", ink: "#2B1622" }, // Pink
  { id: "family_services",    group: "additional", label: "Family / services",  hex: "#A99BC7", ink: "#1F1A2B" }, // Light purple
  { id: "cooking",            group: "additional", label: "Cooking",            hex: "#E8FFE9", ink: "#14331C" }, // Soft green
  { id: "study",              group: "additional", label: "Study",              hex: "#DCE1FF", ink: "#1E2749" }, // Soft light blue
  { id: "animal_care",        group: "additional", label: "Animal care",        hex: "#FFE8F7", ink: "#3A1E30" }, // Soft pink
];

// Convenience groupings for the selector (derived from the single source above).
export const PRIMARY_CATEGORIES = CATEGORIES.filter((c) => c.group === "primary");
export const ADDITIONAL_CATEGORIES = CATEGORIES.filter((c) => c.group === "additional");

// Muted conflict/overlap color (used by the timeline overlap markers).
export const CONFLICT_COLOR = "#B4696B";

// "Part of another event" relationship indicator (NOT a category). Soft light
// blue placeholder — used only to mark that an event belongs to a parent event.
// Replace with the user's final HEX here (single source of truth). [Phase 2 / §4]
export const PARENT_INDICATOR_COLOR = "#8FB8D8";

export const CATEGORY_MAP = Object.fromEntries(
  CATEGORIES.map((c) => [c.id, c])
);

export function getCategory(id) {
  return CATEGORY_MAP[id] || CATEGORY_MAP.free_time;
}

export function isAdditionalCategory(id) {
  return CATEGORY_MAP[id]?.group === "additional";
}

// Convert a #RRGGBB accent into an rgba() string at the given alpha (for soft tints).
export function tint(hex, alpha) {
  const h = (hex || "#CBBEA9").replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
